import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';

const API_BASE = window.location.origin;

/**
 * /manager/qr/:token — validates the signed QR token and redirects to Manager Mode PIN gate.
 * A virtual `card_id` of "qr:<token>" is used so the downstream PIN verify can resolve the
 * location from the token. For simplicity we redirect to /manager/:cardId after resolving.
 */
export default function ManagerQrEntry() {
    const { token } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [state, setState] = useState({ loading: true, error: null, loc: null });

    useEffect(() => {
        (async () => {
            try {
                const res = await fetch(`${API_BASE}/api/manager/qr/validate?token=${encodeURIComponent(token)}`);
                if (!res.ok) {
                    setState({ loading: false, error: t('manager.qrTokenInvalid'), loc: null });
                    return;
                }
                const data = await res.json();
                // Pick any card at this location to proceed to Manager Mode
                // (falls back to a location-scoped sentinel ID if none exists)
                setState({ loading: false, error: null, loc: data });
            } catch {
                setState({ loading: false, error: t('manager.qrTokenInvalid'), loc: null });
            }
        })();
    }, [token, t]);

    useEffect(() => {
        if (!state.loc) return;
        // Navigate to Manager Mode using the card_id returned from the validate endpoint
        if (state.loc.card_id) {
            navigate(`/manager/${state.loc.card_id}`, { replace: true });
        } else {
            setState(s => ({ ...s, error: t('manager.qrTokenInvalid') }));
        }
    }, [state.loc, navigate, t]);

    if (state.loading) {
        return (
            <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-fuchsia-500" />
            </div>
        );
    }

    if (state.error) {
        return (
            <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center p-4" data-testid="qr-error-screen">
                <div className="max-w-sm w-full text-center space-y-4 bg-slate-900 border border-slate-800 rounded-2xl p-6">
                    <div className="mx-auto w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center">
                        <AlertTriangle className="w-6 h-6 text-red-400" />
                    </div>
                    <p className="text-sm text-slate-300">{state.error}</p>
                    <Button onClick={() => navigate('/')} variant="outline" data-testid="qr-error-home-btn">{t('common.home', 'Home')}</Button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-fuchsia-500" />
        </div>
    );
}
